import PropTypes from 'prop-types'
import { useState } from 'react'

import {
  Box,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  makeStyles,
  IconButton,
  DialogActions,
  Button,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { LoadingButton } from 'components'
import { X as XIcon } from 'react-feather'

import schema from './schema'
import styles from './styles'
import * as service from 'service'
import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import { useForm, Controller } from 'react-hook-form'
import theme from 'theme'

const useStyles = makeStyles(styles)

const DialogBankAccountCreation = ({ open, setOpen = () => {}, refresh }) => {
  const [disableButton, setDisableButton] = useState(false)
  const classes = useStyles()
  const snackbar = useSnackbar()

  const { response, isLoading } = useFetch(
    service.intermediador.bankAccounts.listBanks,
  )

  const { control, handleSubmit, errors, setValue } = useForm({
    validationSchema: schema,
    defaultValues: {
      bank: '',
      agency: '',
      number: '',
      titularName: '',
      pixKey: '',
      document: '',
      documentType: '',
    },
  })

  const handleClose = () => {
    setOpen(false)
  }

  const onSubmit = async (data) => {
    setDisableButton(true)
    try {
      await service.intermediador.bankAccounts.create({
        bank: data.bank.id + ' - ' + data.bank.name,
        agency: data.agency,
        number: data.number,
        titularName: data.titularName,
        pixKey: data.pixKey,
        document: data.document,
        documentType: data.documentType,
        status: 'ACTIVE',
      })

      snackbar.open({
        message: `Conta bancária criada com sucesso`,
        variant: 'success',
      })
      setOpen(false)
      refresh()
      setDisableButton(false)
    } catch (error) {
      snackbar.open({
        message: 'Falha ao salvar Conta bancária',
        variant: 'error',
      })
      setDisableButton(false)
    }
  }

  return (
    <Dialog
      open={open}
      PaperProps={{ classes: { root: classes.root } }}
      onClose={handleClose}
      maxWidth="sm"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Nova conta bancária </Typography>
          <IconButton onClick={handleClose}>
            <XIcon color={theme.palette.text.primary} />
          </IconButton>
        </Box>
      </DialogTitle>
      <form id="bankAccountForm" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={8}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                as={
                  <Autocomplete
                    options={isLoading ? [] : response?.data?.data}
                    getOptionLabel={(option) => {
                      if (option.name === undefined) {
                        return ''
                      }
                      return option.id + ' - ' + option.name || ''
                    }}
                    disableClearable
                    getOptionSelected={(option, value) => {
                      if (value === '') {
                        return true
                      }
                      return option.name === value.name
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Instituições financeiras"
                        variant="outlined"
                        error={!!errors.bank}
                        helperText={errors?.bank?.message}
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                }
                control={control}
                onChange={([, data]) => data}
                name="bank"
                mode="onBlur"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="Agência"
                    type="text"
                    color="primary"
                    error={!!errors.agency}
                    helperText={errors?.agency?.message}
                    size="small"
                  />
                }
                control={control}
                name="agency"
                mode="onBlur"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="Número"
                    type="text"
                    color="primary"
                    error={!!errors.number}
                    helperText={errors?.number?.message}
                    size="small"
                  />
                }
                control={control}
                name="number"
                mode="onBlur"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="Chave Pix"
                    type="text"
                    color="primary"
                    error={!!errors.pixKey}
                    helperText={errors?.pixKey?.message}
                    size="small"
                  />
                }
                control={control}
                name="pixKey"
                mode="onBlur"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={7} lg={7}>
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="Nome do titular"
                    type="text"
                    color="primary"
                    error={!!errors.titularName}
                    helperText={errors?.titularName?.message}
                    size="small"
                  />
                }
                control={control}
                name="titularName"
                mode="onBlur"
              />
            </Grid>

            <Grid item xs={12} sm={12} md={5} lg={5}>
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="CNPJ/CPF do titular"
                    type="text"
                    color="primary"
                    error={!!errors.document}
                    helperText={errors?.document?.message}
                    size="small"
                  />
                }
                control={control}
                name="document"
                mode="onBlur"
                onChange={([event]) => {
                  if (event.target.value.length === 14) {
                    setValue('documentType', 'CPF')
                  } else if (event.target.value.length > 14) {
                    setValue('documentType', 'CNPJ')
                  }
                  return helpers.formatters.document(event.target.value)
                }}
              />
              <Controller
                name="documentType"
                as={<input type="hidden" />}
                control={control}
                mode="onBlur"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box pr={1}>
            <Button type="button" variant="outlined" onClick={handleClose}>
              Voltar
            </Button>
          </Box>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            text="Salvar"
            disabled={disableButton}
          />
        </DialogActions>
      </form>
    </Dialog>
  )
}

DialogBankAccountCreation.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  refresh: PropTypes.func,
}

export default DialogBankAccountCreation
